<script setup>
import DefaultInput from "@/components/global-themed/inputs/DefaultInput.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {editableContent, isAdminView} from "@/composables/email-templates/useEmailTemplates";
import TemplateVariables from "@/components/email-templates/partials/TemplateVariables.vue";
import RichTextEditor from "@/components/email-templates/partials/RichTextEditor.vue";
import axios from "axios";
import {useMutation} from "@tanstack/vue-query";
import {errorToast, successToast} from "@/composables/useToasts";
import {templateId} from "@/composables/email-templates/useEmailTemplates";
import {computed} from "vue";

const updateEndpoint = computed(() => {
  return isAdminView.value
    ? `/ajax/v2/admin/mail/template/${templateId.value}/content/`
    : `/ajax/v2/mail/template/${templateId.value}/content/`;
});

const {mutate: handleSubmit, isPending} = useMutation({
  mutationFn: async () => await axios.post(updateEndpoint.value, {
    subject: editableContent.value.subject,
    body: editableContent.value.body,
  }),
  onSuccess: () => {
    successToast('Email content successfully updated.');
  },
  onError: () => {
    errorToast('Failed to save email content. Please try again later.');
  }
});
</script>

<template>
  <div class="tw-bg-white tw-p-8 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-8 tw-w-full">
    <h3 class="tw-text-neutral-900 tw-font-bold tw-text-2xl tw-leading-none">
      What should the email say?
    </h3>

    <DefaultInput
      id="from"
      v-model="editableContent.from_email"
      label="From"
      name="from"
      disabled
    />

    <DefaultInput
      id="subject"
      v-model="editableContent.subject"
      label="Subject"
      name="subject"
    />

    <div class="tw-flex tw-flex-col tw-justify-center tw-gap-y-4">
      <RichTextEditor
        v-model="editableContent.body"
        font="Verdana, Geneva, sans-serif"
      />

      <TemplateVariables />
      <DefaultButton
        class="tw-self-center"
        :disabled="isPending"
        :loading="isPending"
        @click="handleSubmit"
      >
        Update Content
      </DefaultButton>
    </div>
  </div>
</template>

<style scoped>
.placeholders {
  width: 25%;
  margin-top: 10px;
}

.placeholders > .title {
  font-size: 0.75rem;
  line-height: 1.3;
  letter-spacing: -0.1px;
  color: #909096;
  margin-bottom: 0.25rem;
  padding: 7px 10px;
  border-radius: 3px;
  font-weight: 700;
}

/* accordion */
.accordion_body {
  transition: 300ms;
}

.page_accordion .accordion_wrapper.active .accordion_head {
  background-color: #37B2A8;
}

.accordion_head > button {
  margin-left: 1rem
}

.accordion_head .move-page {
  display: none;
}

.accordion_head .delete {
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.8745 4.375L3.12451 4.37501' stroke='%23F34F4F' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M8.125 8.125V13.125' stroke='%23F34F4F' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M11.875 8.125V13.125' stroke='%23F34F4F' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M15.6245 4.375V16.25C15.6245 16.4158 15.5587 16.5747 15.4415 16.6919C15.3242 16.8092 15.1653 16.875 14.9995 16.875H4.99951C4.83375 16.875 4.67478 16.8092 4.55757 16.6919C4.44036 16.5747 4.37451 16.4158 4.37451 16.25V4.375' stroke='%23F34F4F' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M13.125 4.375V3.125C13.125 2.79348 12.9933 2.47554 12.7589 2.24112C12.5245 2.0067 12.2065 1.875 11.875 1.875H8.125C7.79348 1.875 7.47554 2.0067 7.24112 2.24112C7.0067 2.47554 6.875 2.79348 6.875 3.125V4.375' stroke='%23F34F4F' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  width: 1.25rem;
  height: 1.25rem;
  background-size: auto;
  background-position: center;
  background-repeat: no-repeat;
  display: none;
}

.accordion_wrapper.active .accordion_head .delete,
.accordion_wrapper.active .accordion_head .move-page {
  display: block;
}

.accordion_head, .accordion_sub_head {
  cursor: pointer;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: flex-start;
  -webkit-transition: .3s;
  -o-transition: .3s;
  transition: .3s;
  gap: .5rem;
}

.content-block {
  display: flex;
  gap: 1rem;
}

.heading_h4 {
  line-height: 1.42;
  letter-spacing: -0.042em;
  color: #040707;
  font-size: 1.5rem;
  font-weight: 700;
}

@media screen and (max-width: 1200px) {
  .heading_h4 {
    font-size: 1.25rem;
  }
}
</style>
